import { useEffect, useRef, useCallback, useState } from "react";
import { Icons } from "@/components/ui/icons";
import { TextInput } from "@/components/ui/text-input";
import { AgentAvatarPreview } from "@/components/ui/agent-avatar-preview";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { panelsHeaderMessages } from "@/i18n/messages/panels-header";
import { useAgentStore } from "@/stores/agent-store";
import { useAuth } from "@/hooks/use-auth";
import type { AgentAvatarStyleKey } from "@/lib/dicebear-styles";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui/surface-contract";
import { useInputPopoverPlacement } from "./use-input-popover-placement";

interface AgentHirePopupProps {
  projectId: string;
  open: boolean;
  onClose: () => void;
  onOpenSettings?: () => void;
  className?: string;
}

/**
 * Popover listing unassigned agent templates — styled to match
 * the @mention popup (vertical list, same width & position).
 */
export function AgentHirePopup({
  projectId,
  open,
  onClose,
  onOpenSettings,
  className,
}: AgentHirePopupProps) {
  const t = useT(panelsHeaderMessages);
  const tc = useT(commonMessages);
  const popupRef = useRef<HTMLDivElement>(null);
  const style = useInputPopoverPlacement(popupRef, { active: open });
  const { user } = useAuth();
  const avatarStyleKey = (user?.agent_avatar_style ??
    "glass") as AgentAvatarStyleKey;

  const canCreate = useAgentStore((state) => state.specialistCreationEnabled);
  const templates = useAgentStore((s) => s.templates);
  const agents = useAgentStore((s) => s.agents);
  const fetchAllTemplates = useAgentStore((s) => s.fetchAllTemplates);
  const addAgentToProject = useAgentStore((s) => s.addAgentToProject);
  const createAndHireAgent = useAgentStore((s) => s.createAndHireAgent);

  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const createInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      fetchAllTemplates();
      setShowCreate(false);
      setNewName("");
    }
  }, [open, fetchAllTemplates]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open, onClose]);

  const handleHire = useCallback(
    async (templateId: string) => {
      await addAgentToProject(projectId, templateId);
    },
    [projectId, addAgentToProject],
  );

  const handleCreate = useCallback(async () => {
    const trimmed = newName.trim();
    if (!trimmed || creating) return;
    setCreating(true);
    try {
      await createAndHireAgent(projectId, trimmed);
      onClose();
      onOpenSettings?.();
    } finally {
      setCreating(false);
    }
  }, [
    newName,
    creating,
    projectId,
    createAndHireAgent,
    onClose,
    onOpenSettings,
  ]);

  useEffect(() => {
    if (showCreate) createInputRef.current?.focus();
  }, [showCreate]);

  if (!open || !canCreate) return null;

  const assignedTemplateIds = new Set(agents.map((a) => a.agent_template));
  const available = templates.filter(
    (t) => t.status === "available" && !assignedTemplateIds.has(t.id),
  );

  return (
    <div
      ref={popupRef}
      className={cn(
        surfaceRoleClassName("menu"),
        "absolute w-48 overflow-y-auto",
        className,
      )}
      style={style}
      data-testid="agent-hire-popup"
      {...surfaceRoleAttributes("menu", {
        nativeLevel: "root",
        nativeGroup: "menus",
      })}
    >
      {/* Header — matches mention popup */}
      <div className="border-b border-glass-stroke-subtle px-2.5 py-1.5">
        <span
          className="text-appkit-mini font-medium text-muted"
          data-testid="agent-hire-popup-header"
        >
          {t("availableAgents")}
        </span>
      </div>

      {/* Agent list — vertical, same row style as @mention */}
      {available.length === 0 && !showCreate ? (
        <div className="px-2.5 py-3 text-center">
          <span
            className="text-appkit-mini text-muted"
            data-testid="agent-hire-popup-empty"
          >
            {t("noAgentsAvailable")}
          </span>
        </div>
      ) : !showCreate ? (
        <div className="p-1" data-testid="agent-hire-popup-list">
          {available.map((template) => (
            <button
              key={template.id}
              type="button"
              onClick={() => handleHire(template.id)}
              className="denker-glass-hover flex w-full items-center gap-2 rounded-[16px] px-appkit-sm py-1.5 text-left transition-colors"
              data-testid={`agent-hire-btn-${template.id}`}
            >
              <AgentAvatarPreview
                styleKey={avatarStyleKey}
                seed={template.avatar_seed || template.name}
                size={24}
              />
              <span className="text-appkit-small font-medium text-primary">
                {template.name}
              </span>
              <Icons.Plus className="ml-auto h-3 w-3 text-accent" />
            </button>
          ))}
        </div>
      ) : null}

      {/* Inline create form */}
      {showCreate && (
        <div
          className="flex flex-col gap-1.5 p-2"
          data-testid="agent-hire-create-form"
        >
          <TextInput
            ref={createInputRef}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreate();
              if (e.key === "Escape") {
                e.stopPropagation();
                setShowCreate(false);
              }
            }}
            placeholder={t("agentNamePlaceholder")}
            data-testid="agent-hire-create-name-input"
          />
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="flex-1 rounded-control px-2 py-1 text-appkit-mini text-muted transition-colors hover:text-primary"
              data-testid="agent-hire-create-cancel"
            >
              {tc("cancel")}
            </button>
            <button
              type="button"
              onClick={handleCreate}
              disabled={!newName.trim() || creating}
              className="flex-1 rounded-control bg-accent px-2 py-1 text-appkit-mini font-medium text-black transition-opacity disabled:opacity-40"
              data-testid="agent-hire-create-submit"
            >
              {creating ? t("creating") : t("create")}
            </button>
          </div>
        </div>
      )}

      {/* Footer — create + settings links */}
      <div className="flex flex-col gap-1 border-t border-glass-stroke-subtle px-2.5 py-1.5">
        {!showCreate && (
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="denker-glass-hover flex w-full items-center gap-1.5 rounded-[16px] px-1 py-1 text-appkit-mini text-accent transition-colors"
            data-testid="agent-hire-popup-create"
          >
            <Icons.Plus className="h-3 w-3" />
            {t("createAgent")}
          </button>
        )}
        <button
          type="button"
          onClick={onOpenSettings}
          className="denker-glass-hover flex w-full items-center gap-1.5 rounded-[16px] px-1 py-1 text-appkit-mini text-muted transition-colors"
          data-testid="agent-hire-popup-settings"
        >
          <Icons.Settings className="h-3 w-3" />
          {t("agentSettings")}
        </button>
      </div>
    </div>
  );
}
